import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Camera, Check, Upload, UserRound, X } from "lucide-react";
import { requestPlayerPhoto, errorMessage } from "@/lib/api";
import { invalidateScope } from "@/lib/queryClient";
import type { Player, PlayerPhotoRequest } from "@/lib/types";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/components/ui/toast";
import { PlayerAvatar } from "@/features/player-photos/PlayerAvatar";

export type LinkedPlayersStripProps = {
  players: Player[];
  photoRequests?: PlayerPhotoRequest[];
  selectedPlayerId?: string;
  onSelect?: (id: string) => void;
};

type AvatarStyle = "neutral" | "female" | "male";

const AVATAR_STYLES: readonly [AvatarStyle, string][] = [
  ["neutral", "Neutral"],
  ["female", "Female"],
  ["male", "Male"],
];

export function LinkedPlayersStrip({
  players,
  photoRequests = [],
  selectedPlayerId,
  onSelect,
}: LinkedPlayersStripProps) {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [activePlayer, setActivePlayer] = useState<Player | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [avatarStyle, setAvatarStyle] = useState<AvatarStyle | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const latestRequestByPlayer = useMemo(() => {
    const map = new Map<string, PlayerPhotoRequest>();
    for (const request of photoRequests) {
      if (!map.has(request.player_id)) map.set(request.player_id, request);
    }
    return map;
  }, [photoRequests]);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    if (!activePlayer) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setActivePlayer(null);
      setFile(null);
      setAvatarStyle(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activePlayer]);

  if (players.length === 0) {
    return (
      <div className="family-linked-empty" data-testid="feedback-linked-empty">
        <UserRound size={22} aria-hidden />
        <span>
          <strong>No roster player linked yet.</strong>
          <small>Ask your coach to link your account to a player.</small>
        </span>
      </div>
    );
  }

  const reset = () => {
    setActivePlayer(null);
    setFile(null);
    setAvatarStyle(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async () => {
    if (!activePlayer || (!file && !avatarStyle)) return;
    setSubmitting(true);
    try {
      await requestPlayerPhoto(
        activePlayer.id,
        file ? { file } : { avatarStyle: avatarStyle! },
      );
      toast.success("Appearance request sent");
      await invalidateScope();
      reset();
    } catch (err) {
      toast.error("Couldn't send photo request", {
        description: errorMessage(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const selected = players.find((p) => p.id === selectedPlayerId) ?? players[0];
  const selectedPending =
    latestRequestByPlayer.get(selected.id)?.status === "pending";

  return (
    <>
      <div className="space-y-3" data-testid="feedback-linked-strip">
        <button
          type="button"
          className="family-player-photo"
          aria-label="Request photo change"
          title={`Request photo change for ${selected.display_name}`}
          disabled={selectedPending}
          onClick={() => {
            setActivePlayer(selected);
            setFile(null);
            setAvatarStyle(null);
          }}
        >
          <Camera size={20} aria-hidden />
        </button>
        <div className="family-player-choices">
          {players.map((p) => {
          const latest = latestRequestByPlayer.get(p.id);
          const pending = latest?.status === "pending";
          const profile = (
            <>
              <PlayerAvatar player={p} size="sm" />
              <span className="min-w-0">
                <strong className="block">{p.display_name}</strong>
                <small className="block mt-1">
                  {[
                    p.jersey_number ? `#${p.jersey_number}` : null,
                    p.position,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </small>
              </span>
              {players.length > 1 && onSelect ? (
                <Check
                  className="family-player-check"
                  size={16}
                  aria-hidden
                />
              ) : null}
            </>
          );
          return (
            <span
              key={p.id}
              data-feedback-linked-player={p.id}
              data-selected={selectedPlayerId === p.id}
              data-profile={!onSelect}
              className="inline-flex items-center gap-2 rounded border border-rule bg-surface px-2 py-1 text-xs text-ink"
            >
              {onSelect ? (
                <button
                  type="button"
                  aria-pressed={selectedPlayerId === p.id}
                  onClick={() => onSelect(p.id)}
                  className="family-player-choice"
                >
                  {profile}
                </button>
              ) : (
                <div className="family-player-choice">{profile}</div>
              )}
              {pending ? (
                <Badge variant="accent">Photo pending</Badge>
              ) : latest?.status === "rejected" ? (
                <Badge variant="muted">Photo not approved</Badge>
              ) : null}
            </span>
          );
          })}
        </div>
      </div>

      {activePlayer && typeof document !== "undefined"
        ? createPortal(
            <div
              className="family-photo-dialog-backdrop"
              role="dialog"
              aria-modal="true"
              aria-labelledby="family-photo-dialog-title"
              data-testid="feedback-photo-dialog"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) reset();
              }}
            >
              <section className="family-photo-dialog">
                <header>
                  <h2 id="family-photo-dialog-title">
                    Request a player photo change
                  </h2>
                  <button
                    type="button"
                    onClick={reset}
                    aria-label="Close dialog"
                  >
                    <X size={20} aria-hidden />
                  </button>
                </header>
                <div className="family-photo-dialog-body">
                  <div className="family-photo-player">
                    <div className="family-photo-preview">
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt=""
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <PlayerAvatar
                          player={{
                            ...activePlayer,
                            avatar_style:
                              avatarStyle ?? activePlayer.avatar_style,
                            photo_filename: avatarStyle
                              ? null
                              : activePlayer.photo_filename,
                          }}
                          size="lg"
                        />
                      )}
                    </div>
                    <span>
                      <strong>{activePlayer.display_name}</strong>
                      <small>
                        {[
                          activePlayer.jersey_number
                            ? `#${activePlayer.jersey_number}`
                            : null,
                          activePlayer.position,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    </span>
                  </div>
                  <p>
                    Your coach reviews photo and avatar changes before they
                    appear on your player profile.
                  </p>
                  <label className="family-photo-file-label">
                    <span>Choose a photo</span>
                    <span className="family-photo-file-control">
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload size={16} strokeWidth={2} aria-hidden="true" />
                        Choose file
                      </Button>
                      <span>{file?.name ?? "No file chosen"}</span>
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
                      className="sr-only"
                      onChange={(e) => {
                        const selectedFile = e.currentTarget.files?.[0] ?? null;
                        setFile(selectedFile);
                        if (selectedFile) setAvatarStyle(null);
                      }}
                    />
                  </label>
                  <p className="family-photo-hint">
                    PNG, JPG, JPEG or WebP. A coach reviews the request before
                    the photo is saved.
                  </p>
                  <section className="family-avatar-picker">
                    <h3>Or choose a generic avatar</h3>
                    <div role="group" aria-label="Generic avatar">
                      {AVATAR_STYLES.map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          className="player-avatar-choice"
                          aria-pressed={avatarStyle === value}
                          data-testid={`feedback-avatar-${value}`}
                          onClick={() => {
                            setAvatarStyle(value);
                            setFile(null);
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                            }
                          }}
                        >
                          <PlayerAvatar
                            player={{
                              ...activePlayer,
                              photo_filename: null,
                              avatar_style: value,
                            }}
                            size="md"
                          />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                    <p className="family-photo-hint">
                      Choosing an avatar replaces the current photo after your
                      coach approves it.
                    </p>
                  </section>
                </div>
                <footer>
                  <Button
                    type="button"
                    disabled={(!file && !avatarStyle) || submitting}
                    onClick={submit}
                  >
                    Send request
                  </Button>
                  <Button type="button" variant="ghost" onClick={reset}>
                    Cancel
                  </Button>
                </footer>
              </section>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
